import { Box, Flex, Text } from '@modusign/design-system';

import CenterLoadingSpinner from '@app/components/CenterLoadingSpinner';
import josa from '@app/libs/josa';

interface LoadMoreButtonProps {
  dataName: string;
  currentDataCount: number;
  maxDataCount: number;
  size?: 'sm' | 'md';
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  status: 'idle' | 'loading' | 'error' | 'success';
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}

const variantStatuses = ['loading', 'error'] as const;

/**
 * @example
 *
 * const { data, isFetchingNextPage, status, fetchNextPage, hasNextPage } = useInfiniteQuery(...);
 * return (
 *   <LoadMoreButton
 *     currentDataCount={data?.pages.length ?? 0}
 *     dataName="resourceName"
 *     hasNextPage={hasNextPage ?? true}
 *     isFetchingNextPage={isFetchingNextPage}
 *     maxDataCount={data?.pages.at(-1).maxPage ?? 0}
 *     status={status}
 *     onClick={() => fetchNextPage()}
 *   />
 * );
 */
function LoadMoreButton({
  dataName,
  currentDataCount,
  maxDataCount,
  size = 'md',
  hasNextPage,
  isFetchingNextPage,
  status,
  onClick,
}: LoadMoreButtonProps) {
  const [isLoading, isError] = variantStatuses.map((v) => v === status);
  const progress = `(${currentDataCount}/${maxDataCount})`;
  const isSmSize = size === 'sm';

  if (!hasNextPage) {
    return null;
  }

  return (
    <Flex
      alignItems="center"
      as="button"
      bg={isSmSize ? 'transparent' : 'buttonBackgroundNeutralLightest'}
      disabled={isLoading || isFetchingNextPage}
      height={isSmSize ? 34 : 36}
      justifyContent="center"
      my={isSmSize ? undefined : 5}
      position="relative"
      py={3}
      sx={{
        '&:hover': {
          bg: isSmSize ? 'transparent' : 'buttonBackgroundNeutralBase',
          '& .LoadMoreButton__innerText': {
            color: 'textNeutralDark',
          },
        },
        '&:disabled': { bg: isSmSize ? 'transparent' : 'buttonBackgroundNeutralLightest' },
        ...(isError && {
          '&:hover': {
            bg: isSmSize ? 'transparent' : 'buttonBackgroundNeutralLightest',
            '& .LoadMoreButton__retry': {
              color: 'textNeutralDark',
            },
          },
        }),
      }}
      type="button"
      width="100%"
      onClick={onClick}
    >
      {(isLoading || isFetchingNextPage) && <CenterLoadingSpinner size={18} />}
      {isError ? (
        <Text fontSize={size}>
          {`${josa(dataName, '을/를')} 불러오지 못했습니다.`}
          <Text
            className="LoadMoreButton__retry"
            color="textNeutralLight"
            fontSize={size}
            ml={1}
            sx={{ textDecoration: 'underline', textUnderlineOffset: '0.2em' }}
          >
            다시 시도
          </Text>
        </Text>
      ) : (
        <Box sx={{ visibility: isLoading || isFetchingNextPage ? 'hidden' : 'visible' }}>
          <Text
            className="LoadMoreButton__innerText"
            color="textNeutralLight"
            fontSize={size}
            sx={{
              textDecoration: 'underline',
              textUnderlineOffset: '0.2em',
            }}
          >
            더 보기
          </Text>
          <Text
            className="LoadMoreButton__innerText"
            color="textNeutralLight"
            fontSize={size}
            ml={1}
          >
            {progress}
          </Text>
        </Box>
      )}
    </Flex>
  );
}

export default LoadMoreButton;
